/**
 * מסך "אישורי המשך"  —  /followup-approvals
 *
 * מטופלים שסיימו אינטייק פסיכיאטרי וסומנו כזקוקים למעקב. הרכזת רואה את
 * הסיכום של הפסיכיאטר, ומזינה מספר התחייבות שני שפותח את פגישות המעקב.
 *
 * הנתונים מגיעים מהמערכת הישראלית דרך הבקאנד האנגלי - הם לא יושבים כאן.
 */
import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import toast from 'react-hot-toast';
import api from '../services/api';
import {
  FollowupCandidate,
  getFollowupCandidates,
  submitFollowupObligation,
} from '../services/followupService';
import { isValidObligation } from '../utils/israeliValidation';
import { formatDate } from '../utils/dateUtils';

// Followup approval table (updated spec 18/08): the district user sees their
// sub_clinic's patients whose psy intake is done (approved summary,
// yes_followup) and whose followup obligation is still empty, views the
// summary PDF, and enters the followup obligation number.
// Days since the psychiatrist saw them. The list is a queue, not a register:
// somebody waiting nine months and somebody waiting a week both look like one
// row, and the coordinator has no way to tell which to open first.
const daysWaiting = (isoDate?: string | null): number | null => {
  if (!isoDate) return null;
  const then = new Date(isoDate).getTime();
  if (Number.isNaN(then)) return null;
  return Math.floor((Date.now() - then) / 86400000);
};

// Two months without a follow-up obligation is long enough that the referral is
// the thing that stalled, not the person.
const LONG_WAIT_DAYS = 60;

// Days all the way down, never rounded to months: with a full board several
// rows land on the same month and stop being orderable by eye, which is the one
// thing this column exists to do.
const waitingLabel = (days: number): string =>
  days === 1 ? 'יום' : `${days} ימים`;

const FollowupApprovalsPage: React.FC = () => {
  const [rows, setRows] = useState<FollowupCandidate[]>([]);
  const [loading, setLoading] = useState(true);
  const [notWiredYet, setNotWiredYet] = useState(false);
  // Kept apart from an empty list on purpose: "nobody is waiting" and "I could
  // not ask" are opposite facts, and the screen used to show the cheerful one
  // for both. The toast faded, the green stayed, and she would have gone away
  // believing the board was clear while the proxy was down.
  const [loadFailed, setLoadFailed] = useState(false);
  const [entryFor, setEntryFor] = useState<FollowupCandidate | null>(null);
  const [obligation, setObligation] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // The summary is read on screen and never lands on disk. The server already
  // refuses to store the document; handing the coordinator a copy of a clinical
  // summary to keep would undo that at the last step.
  const [pdfFor, setPdfFor] = useState<FollowupCandidate | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    setLoadFailed(false);
    try {
      const candidates = await getFollowupCandidates();
      candidates.sort((a, b) =>
        (a.psy_meeting_date || '').localeCompare(b.psy_meeting_date || ''));
      setRows(candidates);
      setNotWiredYet(false);
    } catch (err: any) {
      // The data pipe from the IL system is pending - treat 404 as "not wired yet"
      if (err?.response?.status === 404) {
        setNotWiredYet(true);
      } else {
        // Anything else - the proxy unconfigured, the IL system down, a timeout -
        // is an answer we did not get, not an empty board.
        setRows([]);
        setLoadFailed(true);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // window.open after an await is swallowed by the popup blocker: the click has
  // aged out by the time the IL system answers, and a blocked window returns
  // null rather than throwing - so the old version failed in total silence.
  const openPdf = async (row: FollowupCandidate) => {
    setPdfFor(row);
    setPdfLoading(true);
    try {
      const res = await api.get(`/leads/followup-candidates/${row.patient_id}/summary-pdf`, {
        responseType: 'blob',
      });
      // Rebuild the blob with the type spelled out. Chrome renders a blob URL
      // only when it knows it is a PDF, and what axios hands back does not
      // always carry the content type through - an untyped blob shows as a
      // blank frame rather than an error.
      const blob = new Blob([res.data], { type: 'application/pdf' });
      setPdfUrl(URL.createObjectURL(blob));
    } catch {
      toast.error('לא ניתן לטעון את הסיכום כרגע');
      setPdfFor(null);
    } finally {
      setPdfLoading(false);
    }
  };

  const closePdf = () => {
    if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    setPdfUrl(null);
    setPdfFor(null);
  };

  const obligationValid = isValidObligation(obligation);

  const submit = async () => {
    if (!entryFor) return;
    setSubmitting(true);
    try {
      await submitFollowupObligation(entryFor.patient_id, obligation);
      toast.success('התחייבות ההמשך נקלטה');
      setEntryFor(null);
      setObligation('');
      load();
    } catch {
      toast.error('שגיאה בשמירת ההתחייבות');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box dir="rtl">
      <Typography variant="h5" sx={{ fontWeight: 600, color: 'primary.dark', mb: 0.5 }}>
        אישורי המשך
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        מטופלים שסיימו פגישת פסיכיאטר וממתינים להתחייבות המשך
      </Typography>

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : loadFailed ? (
        <Alert
          severity="error"
          action={<Button color="inherit" size="small" onClick={load}>נסי שוב</Button>}
        >
          לא ניתן לטעון את הרשימה כרגע. ייתכן שממתינים מטופלים — נסי שוב בעוד רגע.
        </Alert>
      ) : notWiredYet ? (
        <Alert severity="info">
          הרשימה תהיה זמינה עם חיבור נתוני הפגישות — בקרוב
        </Alert>
      ) : rows.length === 0 ? (
        <Alert severity="success">אין מטופלים הממתינים להתחייבות המשך 🎉</Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell align="right">שם</TableCell>
                <TableCell align="right">מזהה</TableCell>
                <TableCell align="right">תעודת זהות</TableCell>
                <TableCell align="right">טלפון</TableCell>
                <TableCell align="right">תאריך פגישת פסיכיאטר</TableCell>
                <TableCell align="right">ממתין</TableCell>
                <TableCell align="right">סיכום</TableCell>
                <TableCell align="right"></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.patient_id} hover>
                  <TableCell align="right">{row.first_name} {row.last_name}</TableCell>
                  <TableCell align="right">{row.hm_id}</TableCell>
                  <TableCell align="right">{row.id_number}</TableCell>
                  <TableCell align="right" sx={{ direction: 'ltr', textAlign: 'right' }}>{row.phone}</TableCell>
                  <TableCell align="right">{formatDate(row.psy_meeting_date)}</TableCell>
                  <TableCell align="right">
                    {(() => {
                      const days = daysWaiting(row.psy_meeting_date);
                      if (days === null) return '—';
                      const long = days >= LONG_WAIT_DAYS;
                      return (
                        <Typography
                          variant="body2"
                          component="span"
                          color={long ? 'error' : 'text.primary'}
                          sx={{ fontWeight: long ? 700 : 400 }}
                        >
                          {waitingLabel(days)}
                        </Typography>
                      );
                    })()}
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      startIcon={<PictureAsPdfIcon />}
                      disabled={!row.summary_pdf_available}
                      onClick={() => openPdf(row)}
                    >
                      צפייה
                    </Button>
                  </TableCell>
                  <TableCell align="right">
                    <Button variant="contained" size="small" onClick={() => setEntryFor(row)}>
                      הזנת התחייבות
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog dir="rtl" open={!!entryFor} onClose={() => setEntryFor(null)} fullWidth maxWidth="xs">
        <DialogTitle>
          התחייבות המשך — {entryFor?.first_name} {entryFor?.last_name}
        </DialogTitle>
        <DialogContent>
          <TextField
            label="מספר התחייבות (טופס 17)"
            value={obligation}
            onChange={(e) => setObligation(e.target.value)}
            inputProps={{ inputMode: 'numeric', maxLength: 9 }}
            error={obligation.length > 0 && !obligationValid}
            helperText={
              obligation.length > 0 && !obligationValid
                ? 'מספר התחייבות חייב להכיל 9 ספרות בדיוק'
                : ' '
            }
            fullWidth
            autoFocus
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEntryFor(null)}>ביטול</Button>
          <Button variant="contained" disabled={!obligationValid || submitting} onClick={submit}>
            {submitting ? 'שומר...' : 'שמירה'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog dir="rtl" open={!!pdfFor} onClose={closePdf} fullWidth maxWidth="md">
        <DialogTitle>
          סיכום פסיכיאטר — {pdfFor?.first_name} {pdfFor?.last_name}
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0, height: '75vh' }}>
          {pdfLoading || !pdfUrl ? (
            <Box display="flex" alignItems="center" justifyContent="center" height="100%">
              <CircularProgress />
            </Box>
          ) : (
            <object
              data={pdfUrl}
              type="application/pdf"
              aria-label="סיכום פסיכיאטר"
              style={{ width: '100%', height: '100%' }}
            >
              <Box p={3}>
                <Typography color="text.secondary">
                  הדפדפן אינו מציג את המסמך. נא לנסות דפדפן אחר.
                </Typography>
              </Box>
            </object>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closePdf}>סגירה</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FollowupApprovalsPage;
